import {
  ConsoleDocumentDetail,
  ConsoleDocumentListItem, // 原始接口返回类型
  getConsoleDocumentApi,
  listConsoleDocumentsApi,
} from '@/api/documents';
import { defineStore } from 'pinia';
import { useI18n } from 'vue-i18n';

interface documentItem // 处理之后的类型
  extends Omit<ConsoleDocumentListItem, 'title' | 'children'> {
  key?: string;
  isLeaf?: boolean;
  title?: string;
  children?: documentItem[];
}
type stasteType = {
  currentDocumentKey: string;
  currentDocument: ConsoleDocumentDetail['doc'] | null;
  documentTree: documentItem[];
  expandedKeys: string[];
  leafKeys: string[];
  locale: string;
  pathMap: Record<string, { path: string[]; item: ConsoleDocumentListItem }>;
  searchResults: string[];
};
const useDocumentStore = defineStore('document', {
  state: (): stasteType => ({
    currentDocumentKey: '',
    currentDocument: null,
    documentTree: [],
    expandedKeys: [],
    leafKeys: [], // 只存储文档的叶子节点
    locale: useI18n().locale.value,
    pathMap: {},
    searchResults: [],
  }),

  actions: {
    formateTree(
      documentList: ConsoleDocumentListItem[],
      path: string[] = []
    ): documentItem[] {
      return documentList.map((item) => {
        const isLeaf = !item.is_dir;
        if (isLeaf) {
          this.leafKeys.push(String(item.doc_id));
        } else {
          this.expandedKeys.push(String(item.doc_id));
        }
        this.pathMap[String(item.doc_id)] = { path, item };
        return {
          ...item,
          title: this.locale === 'zh-CN' ? item.title.zh_cn : item.title.en_us,
          key: String(item.doc_id),
          isLeaf,
          children: !isLeaf
            ? this.formateTree(item.children || [], [
                ...path,
                String(item.doc_id),
              ])
            : undefined,
        };
      });
    },
    findDocumentById(id: string) {
      return this.documentTree.find((item) => item.key === id);
    },
    getParentLabel(id: string) {
      return this.pathMap[id].path.map((item) =>
        this.locale === 'zh-CN'
          ? this.pathMap[item].item.title.zh_cn
          : this.pathMap[item].item.title.en_us
      );
    },
    fetchDocumentList() {
      listConsoleDocumentsApi()
        // @ts-ignore
        .then((res) => {
          if (res.code === 0) {
            this.leafKeys = [];
            this.documentTree = this.formateTree(res.data.results);
            this.selectDocument(this.leafKeys[0]);
          } else {
            this.documentTree = [];
          }
        })
        .catch((err) => {
          console.error(err);
          this.documentTree = [];
        });
    },
    selectDirectory(key: string) {
      if (this.expandedKeys.includes(key)) {
        this.expandedKeys = this.expandedKeys.filter((item) => item !== key);
      } else {
        this.expandedKeys.push(key);
      }
    },
    selectDocument(key: string) {
      if (!key) {
        return;
      }
      if (this.leafKeys.includes(key)) {
        if (this.currentDocumentKey === key) {
          return;
        }
        this.currentDocumentKey = key;
        this.fetchDocumentDetail();
      } else {
        this.selectDirectory(key);
      }
    },
    fetchDocumentDetail() {
      getConsoleDocumentApi({ doc_id: Number(this.currentDocumentKey) })
        .then((res) => {
          if (res.code === 0) {
            this.currentDocument = res.data.doc;
          } else {
            this.currentDocument = null;
          }
        })
        .catch((err) => {
          this.currentDocument = null;
        });
    },
    getNeighbourDocument(key: string) {
      const index = this.leafKeys.indexOf(key);
      if (index === -1) {
        return { next: null, prev: null };
      }
      const nextKey = this.leafKeys[index + 1];
      const prevKey = this.leafKeys[index - 1];
      return {
        next: nextKey ? this.pathMap[nextKey]?.item : null,
        prev: prevKey ? this.pathMap[prevKey]?.item : null,
      };
    },
    searchDocument(content: string) {
      this.searchResults = [];
      if (!content || !this.documentTree.length) {
        return;
      }
      Object.keys(this.pathMap).forEach((key) => {
        const document = this.pathMap[key].item;
        if (
          document.title.zh_cn.includes(content) ||
          document.title.en_us.includes(content)
        ) {
          this.searchResults.push(String(document.doc_id));
          this.pathMap[key].path.forEach((item) => {
            if (!this.expandedKeys.includes(String(item))) {
              this.expandedKeys.push(String(item));
            }
          });
        }
      });
    },
  },
});

export default useDocumentStore;
